import { matchPath } from 'react-router-dom';
import type { IRoute } from '../routes';

/**
 * Resolves which Datadog Team owns the page rendered at `pathname`.
 *
 * Why this exists: the ErrorBoundary that actually catches a render crash is usually the
 * generic one inside `<Page>` (libs/ui-business), which knows nothing about routes. Reporting
 * ownership therefore cannot happen at the `addError` call site — it has to be published as
 * RUM global context whenever the route changes.
 *
 * `routes` is a required argument rather than defaulting to the app's route table: importing
 * that table pulls in the entire lazy-route graph (api-lib, hooks, env config), which would
 * make this function untestable in isolation and drag a large import chain into any consumer.
 *
 * Resolution rules:
 * - The deepest matching route wins, so a child can override its parent's owner.
 * - A route without `team` inherits the nearest ancestor's, which is why tagging a section
 *   root (e.g. safety-dashboard) covers everything nested beneath it.
 * - Returns `undefined` when no matching route declares an owner. Callers must treat that as
 *   "unrouted", not as "no error" — the error is still reported, just without a destination.
 */
export function findRouteTeam(pathname: string, routes: IRoute[], inherited?: string): string | undefined {
  for (const route of routes) {
    const hasChildren = Boolean(route.routes?.length);

    // A parent only needs to match as a prefix for its children to be reachable; a leaf
    // honours its own `exact` so sibling routes sharing a prefix don't claim each other.
    const matched = matchPath(pathname, { path: route.path, exact: hasChildren ? false : (route.exact ?? false) });
    if (!matched) {
      continue;
    }

    const team = route.team ?? inherited;

    if (hasChildren) {
      const childTeam = findRouteTeam(pathname, route.routes as IRoute[], team);
      if (childTeam) {
        return childTeam;
      }
    }

    if (team) {
      return team;
    }
  }

  return undefined;
}
